/**
 * AdminSettings - Portal settings & configuration
 * General, Notifications, Security, Integrations settings (read-only, managed via BOS)
 */

import React, { useState, useEffect } from 'react';
import AdminShell from './AdminShell';
import { adminFetch, getAdminData } from './api';

export default function AdminSettings() {
  const [settings, setSettings] = useState({
    company_name: 'Talk Australia',
    portal_url: 'portal.talkireland.com.au',
    support_email: 'support@talkireland.com.au',
    notifications_new_ticket: true,
    notifications_proposal_accepted: true,
    notifications_contract_expiring: true,
    session_timeout_minutes: 60,
    password_policy_min_length: 12,
    password_policy_requires_special: true,
    two_fa_enabled: false,
    bos_connected: false,
    webhook_url: 'https://api.talkireland.com.au/webhooks/portal',
  });
  const [loading, setLoading] = useState(true);

  const fetchSettings = () => {
    adminFetch('/api/admin/settings')
      .then(r => r.json())
      .then(d => { setSettings(d.settings || {}); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { fetchSettings(); }, []);

  const checkBOSConnection = () => {
    // Check if BOS_CALLBACK_URL is configured
    return !!process.env.REACT_APP_BOS_CALLBACK_URL;
  };

  const s = {
    wrapper: {
      maxWidth: 1000,
      margin: '0 auto',
    },
    section: {
      backgroundColor: '#1A2332',
      borderRadius: 12,
      border: '1px solid #2A3444',
      padding: 28,
      marginBottom: 24,
    },
    sectionTitle: {
      fontSize: 16,
      fontWeight: 600,
      color: '#FFFFFF',
      marginBottom: 20,
      paddingBottom: 16,
      borderBottom: '1px solid #2A3444',
    },
    row: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingBottom: 16,
      marginBottom: 16,
      borderBottom: '1px solid #1A2332',
    },
    rowLast: {
      borderBottom: 'none',
    },
    label: {
      fontSize: 14,
      fontWeight: 500,
      color: '#FFFFFF',
    },
    sublabel: {
      fontSize: 12,
      color: '#8B92A5',
      marginTop: 2,
    },
    value: {
      fontSize: 14,
      color: '#BBBEC7',
      textAlign: 'right',
      maxWidth: 400,
    },
    statusBadge: (active, color) => ({
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: 4,
      fontSize: 12,
      fontWeight: 600,
      textTransform: 'uppercase',
      backgroundColor: `${color}20`,
      color: color,
    }),
    toggle: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      cursor: 'pointer',
    },
    toggleSwitch: (active) => ({
      width: 48,
      height: 24,
      borderRadius: 12,
      backgroundColor: active ? '#00D4AA' : '#2A3444',
      position: 'relative',
      transition: 'background-color 0.2s ease',
      cursor: 'pointer',
    }),
    toggleKnob: (active) => ({
      position: 'absolute',
      top: 2,
      left: active ? 26 : 2,
      width: 20,
      height: 20,
      borderRadius: '50%',
      backgroundColor: '#FFFFFF',
      transition: 'left 0.2s ease',
    }),
    infoBox: {
      backgroundColor: '#0F1729',
      border: '1px solid #0057FF',
      borderRadius: 8,
      padding: 12,
      marginTop: 16,
      fontSize: 12,
      color: '#BBBEC7',
      lineHeight: 1.5,
    },
    noteLabel: {
      fontSize: 11,
      color: '#8B92A5',
      marginTop: 16,
      paddingTop: 16,
      borderTop: '1px solid #1A2332',
    },
  };

  if (loading) {
    return (
      <AdminShell title="Settings">
        <div style={{ padding: 40, textAlign: 'center', color: '#5A6175' }}>Loading...</div>
      </AdminShell>
    );
  }

  return (
    <AdminShell title="Settings">
      <div style={s.wrapper}>
        {/* General Section */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>General</h2>

          <div style={s.row}>
            <div>
              <div style={s.label}>Company Name</div>
              <div style={s.sublabel}>Your organization name</div>
            </div>
            <div style={s.value}>{settings.company_name || 'Talk Australia'}</div>
          </div>

          <div style={s.row}>
            <div>
              <div style={s.label}>Portal URL</div>
              <div style={s.sublabel}>Your vendor portal domain</div>
            </div>
            <div style={s.value}>{settings.portal_url || 'portal.talkireland.com.au'}</div>
          </div>

          <div style={{ ...s.row, ...s.rowLast }}>
            <div>
              <div style={s.label}>Support Email</div>
              <div style={s.sublabel}>Primary contact for vendor inquiries</div>
            </div>
            <div style={s.value}>{settings.support_email || 'support@talkireland.com.au'}</div>
          </div>

          <div style={s.infoBox}>
            These settings are configured through your Business Operating System (BOS) and displayed here for reference. To make changes, please update them in BOS.
          </div>
        </div>

        {/* Notifications Section */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Notifications</h2>

          <div style={s.row}>
            <div>
              <div style={s.label}>New Ticket Created</div>
              <div style={s.sublabel}>Email when a vendor creates a support ticket</div>
            </div>
            <div style={s.toggle}>
              <div style={s.toggleSwitch(settings.notifications_new_ticket)}>
                <div style={s.toggleKnob(settings.notifications_new_ticket)} />
              </div>
              <span style={{ fontSize: 12, color: '#8B92A5' }}>
                {settings.notifications_new_ticket ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>

          <div style={s.row}>
            <div>
              <div style={s.label}>Proposal Accepted</div>
              <div style={s.sublabel}>Email when a vendor accepts a proposal</div>
            </div>
            <div style={s.toggle}>
              <div style={s.toggleSwitch(settings.notifications_proposal_accepted)}>
                <div style={s.toggleKnob(settings.notifications_proposal_accepted)} />
              </div>
              <span style={{ fontSize: 12, color: '#8B92A5' }}>
                {settings.notifications_proposal_accepted ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>

          <div style={{ ...s.row, ...s.rowLast }}>
            <div>
              <div style={s.label}>Contract Expiring</div>
              <div style={s.sublabel}>Email when a vendor contract is about to expire</div>
            </div>
            <div style={s.toggle}>
              <div style={s.toggleSwitch(settings.notifications_contract_expiring)}>
                <div style={s.toggleKnob(settings.notifications_contract_expiring)} />
              </div>
              <span style={{ fontSize: 12, color: '#8B92A5' }}>
                {settings.notifications_contract_expiring ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>

          <div style={s.infoBox}>
            Notification settings are read-only in this view. Manage email preferences through your BOS notification rules.
          </div>
        </div>

        {/* Security Section */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Security</h2>

          <div style={s.row}>
            <div>
              <div style={s.label}>Session Timeout</div>
              <div style={s.sublabel}>Inactive session duration before logout</div>
            </div>
            <div style={s.value}>{settings.session_timeout_minutes || 60} minutes</div>
          </div>

          <div style={s.row}>
            <div>
              <div style={s.label}>Password Policy</div>
              <div style={s.sublabel}>Minimum length and character requirements</div>
            </div>
            <div style={s.value}>
              {settings.password_policy_min_length || 12} characters
              {settings.password_policy_requires_special && ' + special characters'}
            </div>
          </div>

          <div style={{ ...s.row, ...s.rowLast }}>
            <div>
              <div style={s.label}>Two-Factor Authentication</div>
              <div style={s.sublabel}>2FA enforcement status</div>
            </div>
            <span style={s.statusBadge(settings.two_fa_enabled, settings.two_fa_enabled ? '#00D4AA' : '#FFB347')}>
              {settings.two_fa_enabled ? 'Enabled' : 'Disabled'}
            </span>
          </div>

          <div style={s.infoBox}>
            Security policies are managed centrally through your BOS. Changes here will sync across all admin accounts.
          </div>
        </div>

        {/* Integrations Section */}
        <div style={s.section}>
          <h2 style={s.sectionTitle}>Integrations</h2>

          <div style={s.row}>
            <div>
              <div style={s.label}>BOS Connection</div>
              <div style={s.sublabel}>Business Operating System status</div>
            </div>
            <span style={s.statusBadge(checkBOSConnection(), checkBOSConnection() ? '#00D4AA' : '#FF6B6B')}>
              {checkBOSConnection() ? 'Connected' : 'Not Connected'}
            </span>
          </div>

          {!checkBOSConnection() && (
            <div style={s.infoBox}>
              BOS integration is not configured. Set REACT_APP_BOS_CALLBACK_URL environment variable to enable portal-BOS synchronization.
            </div>
          )}

          <div style={{ ...s.row, ...s.rowLast }}>
            <div>
              <div style={s.label}>Webhook URL</div>
              <div style={s.sublabel}>Endpoint for vendor events</div>
            </div>
            <div style={s.value}>
              {settings.webhook_url || 'https://api.talkaustralia.com.au/webhooks/portal'}
            </div>
          </div>

          <div style={s.infoBox}>
            Webhook events are fired when vendors take actions (contracts signed, proposals accepted, tickets updated). Configure event subscriptions in your BOS.
          </div>
        </div>

        {/* Footer Note */}
        <div style={s.noteLabel}>
          Settings displayed above are synced from your Business Operating System. All configuration changes must be made in BOS and will be reflected here within minutes. For support, contact your BOS administrator.
        </div>
      </div>
    </AdminShell>
  );
}
